/**
 * View component for /siteLocations/:siteLocationId
 *
 * Displays a single siteLocation from the 'byId' map in the siteLocation reducer
 * as defined by the 'selected' property
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import actions
import * as siteActions from '../../site/siteActions';
import * as siteLocationActions from '../siteLocationActions';
import * as sponsorActions from '../../sponsor/sponsorActions';
import * as studyActions from '../../study/studyActions';

// import global components
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';
import Pagination from "../../../global/components/helpers/Pagination.js.jsx";
import { SearchInput } from '../../../global/components/forms';

// import utilities
import filterUtils from '../../../global/utils/filterUtils';

// import module components
import SiteLocationLayout from '../components/SiteLocationLayout.js.jsx';
import SiteListItem from '../../site/components/SiteListItem.js.jsx';

// import siteLocation css modules
import siteLocationStyles from '../siteLocationModuleStyles.css';


class AdminSingleSiteLocation extends Base {
  constructor(props) {
    super(props);
    this.state = {
      perPage: 50
      , queryText: this.props.query || ''
    };
    this._bind(
      '_handleQuery'
      , '_handleSetPagination'
      , '_handleSort'
      , '_resetPagination'
      , '_setPerPage'
    );
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    dispatch(siteLocationActions.fetchSingleIfNeeded(match.params.siteLocationId));
    dispatch(sponsorActions.fetchListIfNeeded('all'));
    dispatch(studyActions.fetchListIfNeeded('all'));
    dispatch(siteActions.fetchListIfNeeded('_siteLocation', match.params.siteLocationId));
    dispatch(siteActions.setFilter({query: '', sortBy: 'name'}, '_siteLocation', match.params.siteLocationId));
    dispatch(siteActions.setPagination({page: 1, per: 50}, '_siteLocation', match.params.siteLocationId));
  }

  _handleSort(sortBy) {
    const { dispatch, match, siteList } = this.props;
    let newFilter = siteList.filter;
    if(siteList.filter.sortBy && siteList.filter.sortBy.indexOf("-") < 0) {
      sortBy = "-" + sortBy;
    } else {
      sortBy = sortBy.substring(0)
    }
    newFilter.sortBy = sortBy;
    dispatch(siteActions.setFilter(newFilter, '_siteLocation', match.params.siteLocationId));
  }

  _resetPagination() {
    const { dispatch, match } = this.props;
    dispatch(siteActions.setPagination({
      page: 1,
      per: parseInt(this.state.perPage)
    }, '_siteLocation', match.params.siteLocationId));
  }

  _setPerPage(per) {
    const { dispatch, match, siteList } = this.props;
    let newPagination = siteList.pagination;
    newPagination.per = parseInt(per);
    newPagination.page = 1;
    dispatch(siteActions.setPagination(newPagination, '_siteLocation', match.params.siteLocationId));
    this.setState({perPage: per});
  }

  _handleSetPagination(newPagination) {
    const { dispatch, match } = this.props;
    dispatch(siteActions.setPagination(newPagination, '_siteLocation', match.params.siteLocationId));
  }

  _handleQuery(e) {
    const { dispatch, match, siteList } = this.props;
    // always defaulting the page to page 1 so we can see our results
    let pagination = {};
    pagination.page = 1;
    pagination.per = siteList.pagination.per;
    this._handleSetPagination(pagination);
    // continue query logic
    dispatch(siteActions.setFilter({
      query: e.target.value.toLowerCase()
      , sortBy: siteList.filter.sortBy
    }, '_siteLocation', match.params.siteLocationId));
  }

  render() {
    const {
      location
      , match
      , paginatedList
      , siteList
      , siteStore
      , siteLocationStore
      , sortedList
      , sponsorStore
      , studyStore
    } = this.props;

    /**
     * use the selected.getItem() utility to pull the actual siteLocation object from the map
     */
    const selectedSiteLocation = siteLocationStore.selected.getItem();
    const isEmpty = (
      !siteList
      || !siteList.items
      || siteList.didInvalidate
      || !selectedSiteLocation
    );
    const sortedEmpty = sortedList.length === 0;

    return (
      <div className="flex">
        <section className="section">
          <div className="yt-container">
            <Breadcrumbs links={location.state.breadcrumbs} />
            <h3> Single Site/Team Location </h3>
            {isEmpty ?
              (siteLocationStore.selected.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
              :
              <div style={{ opacity: siteLocationStore.selected.isFetching ? 0.5 : 1 }}>
                <div className="content-container">
                  <div className="yt-row space-between">
                    <p><strong>General location info</strong></p>
                    <div>
                      <Link className="yt-btn x-small info" to={`${match.url}/update`}> Update general info </Link>
                    </div>
                  </div>
                  <hr/>
                  <h2> { selectedSiteLocation.name }
                    <Link className="yt-btn small u-pullRight" to={`${this.props.match.url}/update`}> Update Site/Team Location </Link>
                  </h2>

                  <p>{selectedSiteLocation.desription}</p>
                  <p><strong>Address:</strong></p>
                  <p>{selectedSiteLocation.address.street1}</p>
                  <p>{selectedSiteLocation.address.street2}</p>
                  <p>
                    <span> {selectedSiteLocation.address.city}, </span>
                    <span> {selectedSiteLocation.address.state} </span>
                    <span> {selectedSiteLocation.address.postal}</span>
                    { selectedSiteLocation.address.country !== 'US' ?
                      <span>, {selectedSiteLocation.address.country} </span>
                      :
                      null
                    }
                  </p>

                </div>
                <div className="content-container">
                  <p><strong>Sites registered to this location</strong></p>
                  <div className="yt-toolbar">
                    <div className="yt-tools right">
                      <div className="search">
                        <SearchInput
                          name="query"
                          value={siteList.filter.query}
                          change={this._handleQuery}
                          placeholder="Search..."
                          required={false}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="table-wrapper">
                    <table className="yt-table striped">
                      <caption>
                        { siteList.filter && siteList.filter.query && siteList.filter.query.length > 0 ?
                          <span>Filtered Sites &mdash; {sortedList.length} of {Object.keys(siteList.items).length}</span>
                          :
                            <span>All Sites &mdash; {Object.keys(siteList.items).length}</span>
                        }
                        <div className="per-page-select u-pullRight">
                          <label>Show per page: </label>
                          <select
                            name="numPerPage"
                            onChange={(e) => this._setPerPage(e.target.value)}
                            value={this.state.perPage}
                            >
                            <option value={25}> 25 </option>
                            <option value={50}> 50 </option>
                            <option value={100}> 100 </option>
                          </select>
                        </div>
                      </caption>
                      <thead>
                        <tr>
                          <th className="_20 sortable" onClick={()=> this._handleSort('name')}>
                            Name
                            {siteList.filter.sortBy === 'name' ?
                              <i className="ion ion-ios-arrow-down" />
                              : siteList.filter.sortBy === '-name' ?
                                <i className="ion ion-ios-arrow-up" />
                              : null
                            }
                          </th>
                          <th className="_20 sortable" onClick={()=> this._handleSort('number')}>
                            Number
                            {siteList.filter.sortBy === 'number' ?
                              <i className="ion ion-ios-arrow-down" />
                              : siteList.filter.sortBy === '-number' ?
                                <i className="ion ion-ios-arrow-up" />
                              : null
                            }
                          </th>
                          <th className="_20">Sponsor</th>
                          <th className="_20">Study</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          paginatedList.map((site, i) =>
                            <SiteListItem
                              key={site._id}
                              site={site}
                              sponsor={sponsorStore.byId[site._organization]}
                              study={studyStore.byId[site._group]}
                            />
                          )
                        }
                      </tbody>
                    </table>
                    { sortedEmpty ?
                      null
                      :
                      <Pagination
                        pagination={siteList.pagination}
                        setPagination={this._handleSetPagination}
                        totalPages={ Math.floor(sortedList.length / siteList.pagination.per) + 1}
                      />
                    }
                  </div>
                </div>
              </div>
            }
          </div>
        </section>
      </div>
    )
  }
}

AdminSingleSiteLocation.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store, ownProps) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  const sponsorStore = store.sponsor;
  const studyStore = store.study;
  const studyUserStore = store.studyUser;
  const siteStore = store.site;
  const siteList = siteStore.lists && siteStore.lists._siteLocation ? siteStore.lists._siteLocation[ownProps.match.params.siteLocationId] : null;
  const sponsorList = sponsorStore.selected.getItem() || {};
  const studyList = studyStore.selected.getItem() || {};
  let sortedList = [];
  let paginatedList = [];
  let filteredByQuery = [];
  if (siteList) {

    const pagination = siteList.pagination;
    const filter = siteList.filter;
    const query = filter ? filter.query : '';
    const sortBy = filter ? filter.sortBy : 'name';

    // FILTER BY QUERY
    let queryTestString = ("" + query).toLowerCase().trim();
    // queryTestString = queryTestString.replace(/[^a-zA-Z0-9]/g,''); // replace all non-characters and numbers
    filteredByQuery = siteList && siteList.items ? siteList.items.filter((siteId) => {
      return filterUtils.filterSite(queryTestString, siteStore.byId[siteId], sponsorList, studyList);
    }) : []

    // SORT THE LIST
    sortedList = filteredByQuery.map((item) => {
      let newItem = siteStore.byId[item];
      return newItem;
    });
    switch(sortBy) {
      case 'name':
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['asc']);
        break;
      case '-name':
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['desc']);
        break;
      case 'number':
        sortedList = _.orderBy(sortedList, [item => item.number], ['asc']);
        break;
      case '-number':
        sortedList = _.orderBy(sortedList, [item => item.number], ['desc']);
        break;
      default:
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['asc']);
    }

    // apply pagination
    let start = pagination ? (pagination.page - 1) * pagination.per : 1;
    let end = pagination ? start + pagination.per : 0;
    paginatedList = _.slice(sortedList, start, end);

  }
  return {
    paginatedList
    , siteList
    , siteLocationStore: store.siteLocation
    , siteStore
    , sortedList
    , sponsorStore
    , studyStore
    , studyUserStore
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminSingleSiteLocation)
);



// WEBPACK FOOTER //
// modules/siteLocation/views/AdminSingleSiteLocation.js.jsx